/**
 * QuestionResultCard — per-question analytics for one survey question (VER-109).
 *
 * Categorical questions (rating / multi-select / NPS) render through QuestionChart with
 * a user-selectable view: Bar (default, best for comparison) · Donut (composition) ·
 * Line (distribution shape). VAS shows mean + range; free text shows an expandable list.
 *
 * Mock data only (fed by aggregate.ts). Group-level counts; free-text answers are shown
 * verbatim — prototype data, no real respondents.
 */
import React, { FC, useState } from "react";
import {
    Box,
    Chip,
    Divider,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { BarChartOutlined, DonutLargeOutlined, ShowChartOutlined } from "@mui/icons-material";
import { VCard } from "../../../core/components/VCard";
import type { QuestionAnalytics } from "./aggregate";
import { ChartType, QuestionChart } from "./QuestionChart";

const KIND_LABEL: Record<QuestionAnalytics["kind"], string> = {
    choice: "Rating",
    multi: "Multi-select",
    nps: "NPS",
    vas: "Slider",
    freeText: "Free text",
};

/** Kinds that carry an option distribution and so support the chart switcher. */
const CHARTABLE: ReadonlySet<QuestionAnalytics["kind"]> = new Set(["choice", "multi", "nps"]);

const fmt = (n: number | null | undefined, dp = 1): string =>
    n === null || n === undefined || !Number.isFinite(n) ? "—" : n.toFixed(dp);

const ChartTypeToggle: FC<{ value: ChartType; onChange: (v: ChartType) => void }> = ({ value, onChange }) => (
    <ToggleButtonGroup
        size="small"
        exclusive
        value={value}
        onChange={(_e, v: ChartType | null) => v && onChange(v)}
        aria-label="Chart type"
    >
        <ToggleButton value="bar" aria-label="Bar chart">
            <Tooltip title="Bar">
                <BarChartOutlined fontSize="small" />
            </Tooltip>
        </ToggleButton>
        <ToggleButton value="pie" aria-label="Donut chart">
            <Tooltip title="Donut">
                <DonutLargeOutlined fontSize="small" />
            </Tooltip>
        </ToggleButton>
        <ToggleButton value="line" aria-label="Line chart">
            <Tooltip title="Line">
                <ShowChartOutlined fontSize="small" />
            </Tooltip>
        </ToggleButton>
    </ToggleButtonGroup>
);

const FreeTextAnswers: FC<{ answers: string[] }> = ({ answers }) => {
    const [open, setOpen] = useState(false);
    if (answers.length === 0) {
        return (
            <Typography variant="body2" color="textSecondary">
                No written answers.
            </Typography>
        );
    }
    return (
        <Box>
            <Typography
                component="button"
                onClick={() => setOpen((o) => !o)}
                variant="body2"
                sx={{
                    background: "none",
                    border: "none",
                    p: 0,
                    cursor: "pointer",
                    color: "primary.main",
                    fontWeight: 600,
                }}
            >
                {open
                    ? "Hide answers"
                    : `Show ${answers.length} written ${answers.length === 1 ? "answer" : "answers"}`}
            </Typography>
            {open && (
                <Stack spacing={1} mt={1}>
                    {answers.map((answer, i) => (
                        <Typography
                            key={i}
                            variant="body2"
                            sx={{ pl: 2, borderLeft: 3, borderColor: "primary.main", color: "text.secondary" }}
                        >
                            {answer}
                        </Typography>
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export const QuestionResultCard: FC<{ result: QuestionAnalytics; index: number }> = ({ result, index }) => {
    const theme = useTheme();
    const [chartType, setChartType] = useState<ChartType>("bar");
    const { question, kind, answered } = result;

    // NPS uses semantic colours (promoter/passive/detractor); other categoricals a teal ramp.
    const colors =
        kind === "nps"
            ? [theme.palette.success.main, theme.palette.warning.main, theme.palette.error.main]
            : [
                  theme.palette.primary.main,
                  theme.palette.info.main,
                  theme.palette.primary.light,
                  theme.palette.info.light,
                  theme.palette.secondary.light,
                  theme.palette.primary.dark,
              ];

    return (
        <VCard variant="outlined" sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {index}. {question.text}
                </Typography>
                <Stack direction="row" alignItems="center" gap={1} flexShrink={0}>
                    <Chip size="small" variant="outlined" label={KIND_LABEL[kind]} />
                    {CHARTABLE.has(kind) && result.distribution && (
                        <ChartTypeToggle value={chartType} onChange={setChartType} />
                    )}
                </Stack>
            </Stack>

            {kind === "nps" && result.nps && (
                <Stack direction="row" alignItems="baseline" gap={1} mb={1.5}>
                    <Typography variant="h4" sx={{ color: "primary.main", fontWeight: 700 }}>
                        {result.nps.score}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Net Promoter Score · avg {fmt(result.average)}
                    </Typography>
                </Stack>
            )}

            {CHARTABLE.has(kind) && result.distribution && (
                <QuestionChart rows={result.distribution} chartType={chartType} colors={colors} answered={answered} />
            )}

            {kind === "choice" && (
                <Typography variant="body2" color="textSecondary" mt={1}>
                    Average: <strong>{fmt(result.average)}</strong>
                </Typography>
            )}

            {kind === "vas" && (
                <Stack direction="row" alignItems="baseline" gap={1}>
                    <Typography variant="h4" sx={{ color: "primary.main", fontWeight: 700 }}>
                        {fmt(result.average)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        average{result.range ? ` · range ${result.range.min}–${result.range.max}` : ""}
                    </Typography>
                </Stack>
            )}

            {kind === "freeText" && <FreeTextAnswers answers={result.textAnswers ?? []} />}

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="textSecondary">
                {answered} {answered === 1 ? "response" : "responses"}
            </Typography>
        </VCard>
    );
};
